import { useEffect } from 'react';

import { THEMES } from './constants/portfolio';

// Progressive carousel image pipeline, all at the DOM level because the
// web-shared-components templates render plain <img> tags off images[0]:
//
// 1. Batching — the theme carousels mount every slide's <img> at once, which
//    used to fire the whole gallery's 2K downloads in parallel. Carousel
//    <img>s are parked on a transparent placeholder as they are discovered
//    (MutationObserver microtasks run before first paint) and at most
//    MAX_CONCURRENT_LOADS of them fetch their 2K tier at a time, refilling
//    in slide order as loads finish. A parked slide that becomes visible
//    jumps the queue and loads immediately.
// 2. 4K upgrade — once a slide's 2K is on screen the 4K tier is fetched and
//    swapped in place; the browser keeps the 2K bitmap until the 4K decodes,
//    giving "2K placeholder -> 4K" for free. Off-screen slides never
//    intersect the viewport, so they don't pull 4K.
//
// Per-theme wiring:
//  - MOODIE: grid list and lightbox share images[0] (1024), so the lightbox
//    slides (the only Moodie <img>s inside react-slick's .slick-slide) are
//    parked with their 2K url instead — the lightbox never shows the 1024
//    grid asset — while the grid stays 1024
//  - ADAMS: gallery slides only; gated to /gallery/ because the home folder
//    carousel is also react-slick showing the same 2K urls and must stay 2K
//  - NEWTON: the gallery is a scroll box of plain <img>s (no slick), so it
//    is gated to /gallery/ only

const isSlideshowViewport = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(min-width: 768px)').matches;

// All three theme carousels upgrade the same way once on screen
const UPGRADE_CHAIN = ['size_2048', 'size_4k'];

// Themes whose carousel initially mounts with a url below 2K: those <img>s
// are parked with the 2K url instead of the one they mounted with
const REBASE_CHAINS = {
  [THEMES.MOODIE]: ['size_1024', 'size_2048'],
};

// At most this many carousel images download their 2K tier concurrently
const MAX_CONCURRENT_LOADS = 5;

const PLACEHOLDER_SRC = 'data:image/svg+xml,'
  + '%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22'
  + '%20width%3D%221%22%20height%3D%221%22%2F%3E';

const QUEUED_ATTR = 'data-hires-queued-src';
const LOADING_ATTR = 'data-hires-loading';

// Map of rendered url -> next tier url for every carousel photo. Tiers that
// resolve to the same url (test env aliases sizes) are skipped.
const buildUrlMap = (portfolio, chain) => {
  const map = new Map();
  if (!chain || !isSlideshowViewport()) return map;
  (portfolio?.folders || []).forEach((folder) => {
    (folder?.photos?.edges || []).forEach((edge) => {
      const urls = edge?.node?.urls;
      if (!urls) return;
      for (let i = 0; i < chain.length - 1; i += 1) {
        const from = urls[chain[i]];
        const to = urls[chain[i + 1]];
        if (from && to && from !== to && !map.has(from)) {
          map.set(from, to);
        }
      }
    });
  });
  return map;
};

const isCarouselImage = (img, themeName) => {
  if (themeName === THEMES.MOODIE) return !!img.closest('.slick-slide');
  if (!window.location.pathname.includes('/gallery/')) return false;
  if (themeName === THEMES.ADAMS) return !!img.closest('.slick-slide');
  return true;
};

const useCarouselHiResUpgrade = (portfolio) => {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined'
      || typeof MutationObserver === 'undefined') return undefined;
    const themeName = portfolio?.theme?.name?.toUpperCase();
    const upgrades = buildUrlMap(portfolio, UPGRADE_CHAIN);
    if (!upgrades.size) return undefined;
    const rebases = buildUrlMap(portfolio, REBASE_CHAINS[themeName]);
    const downgrades = new Map();
    upgrades.forEach((to, from) => downgrades.set(to, from));
    rebases.forEach((to, from) => downgrades.set(to, from));
    const failedUrls = new Set();

    const queue = [];
    const inFlight = new Set();

    const startLoad = (img) => {
      const realSrc = img.getAttribute(QUEUED_ATTR);
      img.removeAttribute(QUEUED_ATTR);
      img.setAttribute(LOADING_ATTR, '1');
      inFlight.add(img);
      img.setAttribute('src', realSrc);
    };

    const pump = () => {
      // Detached <img>s never bubble load up to document, so their slot has
      // to be reclaimed here (e.g. the lightbox closed mid-batch)
      inFlight.forEach((img) => {
        if (!img.isConnected) {
          img.removeAttribute(LOADING_ATTR);
          inFlight.delete(img);
        }
      });
      while (inFlight.size < MAX_CONCURRENT_LOADS && queue.length) {
        const img = queue.shift();
        if (img.isConnected && img.hasAttribute(QUEUED_ATTR)) startLoad(img);
      }
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        // A parked slide that came on screen loads now, ahead of the queue
        if (target.hasAttribute(QUEUED_ATTR)) {
          startLoad(target);
          return;
        }
        // Entries recorded before unobserve() are still delivered on the
        // next callback: without this guard a slide that just started its
        // 2K fetch would jump straight to 4K off such a stale entry
        if (!target.complete || target.hasAttribute(LOADING_ATTR)) return;
        const next = upgrades.get(target.getAttribute('src'));
        if (next && !failedUrls.has(next)) target.setAttribute('src', next);
      });
    });

    const park = (img, realSrc) => {
      img.setAttribute(QUEUED_ATTR, realSrc);
      img.setAttribute('src', PLACEHOLDER_SRC);
      queue.push(img);
      observer.observe(img);
    };

    // Route a discovered <img> into the pipeline. Runs for DOM insertions
    // (before their first paint) and for the initial sweep.
    const handleImg = (img) => {
      // Parked by a previous run of this effect (portfolio identity changes
      // on re-render): re-adopt it, its queued url is still in the dataset
      if (img.hasAttribute(QUEUED_ATTR)) {
        queue.push(img);
        observer.observe(img);
        return;
      }
      const src = img.getAttribute('src');
      const rebased = rebases.get(src);
      if (rebased) {
        if (!failedUrls.has(rebased) && isCarouselImage(img, themeName)) {
          park(img, rebased);
        }
        return;
      }
      if (!upgrades.has(src) || !isCarouselImage(img, themeName)) return;
      if (img.complete) {
        // Already rendered (e.g. cached): only wait for 4K visibility
        observer.observe(img);
        return;
      }
      if (img.hasAttribute(LOADING_ATTR)) {
        // Pumped by a previous run and still downloading: keep its slot
        inFlight.add(img);
        return;
      }
      park(img, src);
    };

    // load/error don't bubble, but they do reach document in capture phase
    const releaseSlot = (img) => {
      if (!img.hasAttribute(LOADING_ATTR)) return;
      img.removeAttribute(LOADING_ATTR);
      inFlight.delete(img);
      pump();
    };

    const onLoad = (event) => {
      const img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      releaseSlot(img);
      if (!upgrades.has(img.getAttribute('src'))) return;
      if (!isCarouselImage(img, themeName)) return;
      observer.observe(img);
    };

    // A failed hi-res fetch would blank the <img>, so remember the broken url
    // and drop back to the tier that was already on screen
    const onError = (event) => {
      const img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      releaseSlot(img);
      const current = img.getAttribute('src');
      const previous = downgrades.get(current);
      if (!previous) return;
      failedUrls.add(current);
      img.setAttribute('src', previous);
    };

    document.addEventListener('load', onLoad, true);
    document.addEventListener('error', onError, true);

    // MutationObserver microtasks run before the inserted nodes first paint,
    // so carousel <img>s are parked without flashing the url they mounted
    // with (and Moodie's lightbox never shows the 1024 grid asset)
    const insertObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.tagName === 'IMG') handleImg(node);
          else node.querySelectorAll('img').forEach(handleImg);
        });
      });
      pump();
    });
    insertObserver.observe(document.body, { childList: true, subtree: true });

    // Images already in the DOM (direct entry on a gallery url) were
    // inserted before this effect attached its observers, so sweep them in
    Array.prototype.forEach.call(document.images, handleImg);
    pump();

    return () => {
      document.removeEventListener('load', onLoad, true);
      document.removeEventListener('error', onError, true);
      observer.disconnect();
      insertObserver.disconnect();
    };
  }, [portfolio]);
};

export default useCarouselHiResUpgrade;
